"use client";

import { Fragment } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconCheck from "@/app/(components)/generics/Icons/IconCheck";
import CTA from "@/app/(components)/specifics/pages/Home/components/CTA/CTA";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { HomeCtaAction } from "@/app/types/common/HomeCtaAction";
import { Credits } from "@/app/vars/Credits";
import styles from "./Hero.module.css";

type HeroKeys = {
  supTitle: TKey;
  secondaryCta?: TKey;
  pricing?: TKey;
  title1: TKey;
  title2: TKey;
  title3_1: TKey;
  title3_2: TKey;
  title3_3: TKey;
  title3_4: TKey;
  text1: TKey;
  text2: TKey;
};

type Props = DefaultProps & {
  keys: HeroKeys;
};

export default function Hero(props: Props) {
  const T = useT();
  const { params } = props;
  const has = (key: TKey) => T(key) !== key && T(key) !== "";
  const renderMarked = (text: string, className: string) =>
    text.split("\n").map((line, lineIndex) => (
      <Fragment key={`line-${lineIndex}`}>
        {lineIndex > 0 && (
          <>
            <br className={styles.lineBreak} />{" "}
          </>
        )}
        {line.split("**").map((part, i) =>
          i % 2 === 1 ? (
            <span key={`${lineIndex}-${i}`} className={className}>
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </Fragment>
    ));
  const renderHighlighted = (text: string) =>
    renderMarked(text, `${styles.highlight} ${styles.titleHighlight}`);

  return (
    <div className={styles.container}>
      <div className={styles.glowBlob}></div>
      <div className={styles.content}>
        <div className={styles.mainWrapper}>
          <div className={styles.main}>
            <div className={styles.headline}>
              <div className={`${styles.badgeContainer} ${styles.animateSlideUp}`}>
                <div className={styles.badge}>
                  <span className={styles.dot}></span>
                  <span>{T(props.keys.supTitle)}</span>
                </div>
              </div>

              <h1 className={styles.title}>
                {has(props.keys.title1) && (
                  <span className={`${styles.titleLine} ${styles.animateSlideUp} ${styles.delay1}`}>
                    {renderHighlighted(T(props.keys.title1))}
                  </span>
                )}
                {has(props.keys.title2) && (
                  <span className={`${styles.titleLine} ${styles.animateSlideUp} ${styles.delay2}`}>
                    {renderHighlighted(T(props.keys.title2))}
                  </span>
                )}
                {(has(props.keys.title3_1) ||
                  has(props.keys.title3_2) ||
                  has(props.keys.title3_3) ||
                  has(props.keys.title3_4)) && (
                  <span
                    className={`${styles.titleLine} ${styles.titleEmphasized} ${styles.animateSlideUp} ${styles.delay3}`}
                  >
                    {has(props.keys.title3_1) && T(props.keys.title3_1)}
                    {has(props.keys.title3_2) && (
                      <span className={`${styles.highlight} ${styles.titleHighlight}`}>
                        {T(props.keys.title3_2)}
                      </span>
                    )}
                    {has(props.keys.title3_3) && T(props.keys.title3_3)}
                    {has(props.keys.title3_4) && (
                      <span className={`${styles.highlight} ${styles.titleHighlight}`}>
                        {T(props.keys.title3_4)}
                      </span>
                    )}
                  </span>
                )}
              </h1>
            </div>

            <p className={`${styles.text} ${styles.animateSlideUp} ${styles.delay4}`}>
              {renderMarked(T(props.keys.text1), styles.textAccent)}
              {has(props.keys.text2) && (
                <span className={styles.highlight}>{T(props.keys.text2)}</span>
              )}
            </p>

            <div className={`${styles.ctaBlock} ${styles.animateSlideUp} ${styles.delay5}`}>
              <div className={styles.ctaRow}>
                <div className={styles.ctaWrapper}>
                  <div className={styles.ctaGlow}></div>
                  <CTA
                    params={params}
                    hideIcon
                    min={false}
                    action={HomeCtaAction.SIGN_UP}
                    ctaKey={TKey.HOME_CTA_START_FREE}
                  />
                </div>
                {props.keys.secondaryCta && (
                  <Button
                    text={T(props.keys.secondaryCta)}
                    type={ButtonType.OUTLINE}
                    isLink={false}
                    min={false}
                    onClick={() => ModalStore.getState().openDemo()}
                  />
                )}
              </div>
              {props.keys.pricing && (
                <p className={styles.pricing}>
                  <IconCheck color="var(--color-green-2)" />
                  {String(T(props.keys.pricing)).replace(
                    "{{credits}}",
                    String(Credits.DEFAULT_FREE_CREDITS),
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
